/**
 * AvailabilityInspectPopoverContent — unified read/inspect popover for an
 * occupied availability-grid cell (option, soft/hard reservation, sold, or
 * admin-blocked).
 *
 * Part of the unified INSPECT-vs-SELECT interaction proposal (see
 * apps/mockups/src/screens/availability-unified.mockup.tsx). Scaffolded here
 * ahead of the real spec so the mockup can consume it via the standard
 * mockup -> apps/web dependency direction.
 *
 * The concrete action buttons (approve, sell, edit dates, cancel, force
 * release, etc.) are NOT committed business logic yet — the proposal is
 * still under review — so they are passed in via the `actions` slot rather
 * than hardcoded here. Same for `waitlistAction`, which the caller wires to
 * the existing queue join/leave flow (see `CellQueueBlock` in
 * `availability-cell-popover.tsx`) once this popover replaces it.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Users } from 'lucide-react';
import { Badge, type BadgeProps } from '@ooh/ui/components/badge';

// TODO: Move to @ooh/contracts once the availability domain schemas exist
// (see the same TODO on CellState/CellSelection in ./availability-types.ts).
export type AvailabilityInspectStatus = 'option' | 'soft' | 'hard' | 'sold' | 'blocked';

// Reuses the same semantic Badge variants as ReservationStatusBadge
// (`ReservationStatusBadge` in @ooh/screens) instead
// of raw Tailwind colors, so this chip stays in sync with the design
// system's status palette.
const STATUS_BADGE_VARIANTS: Record<AvailabilityInspectStatus, BadgeProps['variant']> = {
  option: 'outline',
  soft: 'info',
  hard: 'warning',
  sold: 'success',
  blocked: 'secondary',
};

export interface AvailabilityStatusBadgeProps {
  status: AvailabilityInspectStatus;
}

export function AvailabilityStatusBadge({ status }: AvailabilityStatusBadgeProps) {
  const { t } = useTranslation('inventory');
  return (
    <Badge variant={STATUS_BADGE_VARIANTS[status]}>
      {t(`availability.inspect.status.${status}`)}
    </Badge>
  );
}

export interface AvailabilityInspectPopoverContentProps {
  status: AvailabilityInspectStatus;
  unitLabel: string;
  dateRangeLabel: string;
  holder?: string;
  client?: string;
  /** Offer link/name markup — caller-supplied so mockup and real routing can differ. */
  offerNode?: ReactNode;
  source?: string;
  ttlLabel?: ReactNode;
  waitlistCount?: number;
  /** Join/leave-queue trigger — caller-supplied, wired to the real queue flow. */
  waitlistAction?: ReactNode;
  /**
   * Richer queue section (CU-86cb7ewja) — the caller's own queue entries
   * (rendered via the shared `ReservationQueueList`, one row per identity)
   * plus the join/"Dodaj wpis dla innego klienta" affordance, all built by
   * `QueueJoinPanel`. Takes over the ENTIRE queue area when supplied — the
   * caller passes EITHER this OR `waitlistCount`/`waitlistAction`, never
   * both.
   */
  queueSection?: ReactNode;
  /**
   * Additional stacked entries (a cell with more than one coexisting entry,
   * e.g. SOLD + OPTION) rendered below the dominant entry's info block — see
   * the `unified-availability-interaction` capability's "Stacked cell opens
   * the consolidated INSPECT popover" scenario. The date range and actions
   * still describe the whole cell, not each secondary entry individually.
   */
  secondaryEntries?: ReactNode;
  /** Status-specific action buttons — caller-supplied (see file header). */
  actions: ReactNode;
}

export function AvailabilityInspectPopoverContent({
  status,
  unitLabel,
  dateRangeLabel,
  holder,
  client,
  offerNode,
  source,
  ttlLabel,
  waitlistCount,
  waitlistAction,
  queueSection,
  secondaryEntries,
  actions,
}: AvailabilityInspectPopoverContentProps) {
  const { t } = useTranslation(['inventory', 'reservations']);
  // CU-86cb7ewja: no longer requires `waitlistAction` — the count header now also
  // renders when `queueSection` (not `waitlistAction`) owns the join/leave affordance.
  const showWaitlist = waitlistCount != null;

  return (
    <div className="p-3" data-testid="availability-inspect-popover">
      <div className="mb-1.5">
        <AvailabilityStatusBadge status={status} />
      </div>
      <p className="text-2xs text-muted-foreground mb-0.5">{unitLabel}</p>
      <p className="text-xs font-medium text-foreground mb-2.5">{dateRangeLabel}</p>

      <div className="space-y-0.5 mb-2.5">
        {holder ? (
          <p className="text-2xs text-muted-foreground">
            {t('availability.inspect.holderLabel')}:{' '}
            <span className="text-foreground font-medium">{holder}</span>
          </p>
        ) : null}
        {client ? (
          <p className="text-2xs text-muted-foreground">
            {t('availability.inspect.clientLabel')}:{' '}
            <span className="text-foreground font-medium">{client}</span>
          </p>
        ) : null}
        {offerNode ? (
          <p className="text-2xs text-muted-foreground flex items-center gap-1">
            {t('availability.inspect.offerLabel')}: {offerNode}
          </p>
        ) : null}
        {source ? (
          <p className="text-2xs text-muted-foreground">
            {t('availability.inspect.sourceLabel')}:{' '}
            <span className="text-foreground font-medium">{source}</span>
          </p>
        ) : null}
        {ttlLabel ? (
          <p className="text-2xs text-muted-foreground flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            {t('availability.inspect.expiresLabel')}:{' '}
            <span
              className="text-foreground font-medium"
              data-testid="availability-inspect-ttl-label"
            >
              {ttlLabel}
            </span>
          </p>
        ) : null}
      </div>

      {secondaryEntries}

      {showWaitlist ? (
        <div className="flex items-center justify-between gap-2 bg-muted/50 border border-border/60 px-2 py-1.5 mb-2.5">
          <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <Users className="h-3 w-3" />
            {t('reservations:waitlist.sectionTitle', { count: waitlistCount })}
          </span>
          {waitlistAction}
        </div>
      ) : null}

      {queueSection ? <div className="mb-2.5">{queueSection}</div> : null}

      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">{actions}</div>
    </div>
  );
}
